import { useQuery } from '@apollo/client';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ParentType } from '@risksmart-app/domain/src/types/consts/index';
import { GetFormConfigurationByParentTypeDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

import { useGetFormConfigurationByParentType as useGetFormConfigurationByParentTypeTRPC } from './useGetFormConfigurationByParentTypeTRPC';

export const useGetFormConfigurationByParentType = (options: {
  variables: { parentTypes: ParentType[] };
  isSkipped?: boolean;
  fetchPolicy?: 'cache-first' | 'no-cache' | 'network-only';
}) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const { addNotification } = useNotifications();
  const {
    variables: { parentTypes },
    isSkipped = false,
    fetchPolicy = 'no-cache',
  } = options;

  const {
    data: graphqlData,
    loading: graphqlLoading,
    refetch: graphqlRefetch,
    error: graphqlError,
  } = useQuery(GetFormConfigurationByParentTypeDocument, {
    variables: { parentTypes: parentTypes },
    fetchPolicy,
    skip: trpcEnabled || isSkipped, // Skip GraphQL query when TRPC is enabled
    onError: (error) => {
      if (!trpcEnabled) {
        addNotification({
          type: 'error',
          content: error.message,
        });
      }
    },
  });

  const {
    data: trpcData,
    loading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useGetFormConfigurationByParentTypeTRPC(
    parentTypes,
    trpcEnabled ? isSkipped : true
  );

  // Return appropriate data based on feature flag
  if (trpcEnabled) {
    return {
      loading: trpcLoading,
      data: trpcData,
      refetch: () => trpcRefetch(),
      error: trpcError,
    };
  }

  return {
    loading: graphqlLoading,
    data: graphqlData,
    refetch: () => graphqlRefetch(),
    error: graphqlError,
  };
};
